import { useId, useState } from 'react'
import { Icon } from '../../components/Icon'
import type { NotePreview } from './notePreview'

type Props = {
  /** What the note is, so the owner sees what would be filed. */
  preview?: NotePreview
  title?: string
  purpose?: string
  count: number
  busy: boolean
  onAdd: () => void
  onChoose: () => void
  onDismiss: () => void
}

/** The note being offered: its picture (or its kind's icon), its name and a line of what it says. */
export function NoteGlance({ preview, className }: { preview: NotePreview; className: string }) {
  const [broken, setBroken] = useState(false)
  return <div className={className}>
    <span className={`${className}-art`} aria-hidden="true">{preview.image && !broken
      ? <img src={preview.image} alt="" crossOrigin="anonymous" referrerPolicy="no-referrer" loading="lazy" decoding="async" onError={() => setBroken(true)}/>
      : <Icon name="document"/>}</span>
    <span className={`${className}-text`}>
      <strong>{preview.title}</strong>
      {preview.detail ? <small>{preview.detail}</small> : null}
    </span>
  </div>
}

export function SmartContextSuggestion({ preview, title, purpose, count, busy, onAdd, onChoose, onDismiss }: Props) {
  const [helpOpen, setHelpOpen] = useState(false)
  const headingId = useId(), helpId = useId()
  const usefulPurpose = purpose?.trim() && purpose.trim().toLocaleLowerCase() !== title?.trim().toLocaleLowerCase()
    && purpose.trim() !== 'Notes you brought together.'

  return <>
  {/* Dims what is behind the sheet so it stands out on any screen; it never blocks a tap. */}
  <div className="smart-context-suggestion__scrim" aria-hidden="true"/>
  <aside className="smart-context-suggestion visual-viewport-bottom" role="region" aria-labelledby={headingId}>
    <div className="smart-context-suggestion__header">
      <h2 id={headingId}><Icon name="context"/>Suggested Context</h2>
      <button type="button" className="btn btn-ghost btn-square smart-context-suggestion__help-toggle"
        aria-label="What are Contexts?" aria-expanded={helpOpen} aria-controls={helpId}
        onClick={() => setHelpOpen(open => !open)}><Icon name="help"/></button>
    </div>
    <p id={helpId} className="smart-context-suggestion__help" hidden={!helpOpen}>
      Contexts are smart, condensed views of notes around a topic or goal. You can view the same Context in different ways. Your original notes remain unchanged.
    </p>
    {preview ? <NoteGlance preview={preview} className="smart-context-suggestion__note"/> : null}
    <div className="smart-context-suggestion__choice">
      {preview && <small className="smart-context-suggestion__label">{count === 1 ? 'Add to' : 'Fits'}</small>}
      <strong>{count === 1 ? title || 'Suggested Context' : `${count} Contexts may fit this note`}</strong>
      {count === 1 && usefulPurpose && <small>{purpose}</small>}
    </div>
    <div className="smart-context-suggestion__actions">
      <button type="button" className="btn btn-primary smart-context-suggestion__primary" disabled={busy}
        onClick={count === 1 ? onAdd : onChoose}>{busy ? 'Adding…' : count === 1 ? 'Add to Context' : 'Review Contexts'}</button>
      <div className="smart-context-suggestion__secondary">
        {count === 1 && <button type="button" className="btn btn-ghost" disabled={busy} onClick={onChoose}>Choose another</button>}
        <button type="button" className="btn btn-ghost" disabled={busy} onClick={onDismiss}>Dismiss</button>
      </div>
    </div>
  </aside>
  </>
}

/**
 * The narrow form, shown at the top of a note that is still open (one that arrived by Share): one line, one tap to add,
 * and out of the way otherwise. It exists only when the fit is strong, so it never asks to be dismissed to be ignored.
 */
export function SmartContextSuggestionBar({ preview, title, count, busy, onAdd, onChoose, onDismiss }: Pick<Props, 'preview' | 'title' | 'count' | 'busy' | 'onAdd' | 'onChoose' | 'onDismiss'>) {
  const label = count === 1 ? title || 'Suggested Context' : `${count} Contexts may fit`
  const question = count === 1 ? `Add to ${label}?` : label
  // The note above, what to do with it below: "Digger" / "Add to Movies and TV?".
  return <aside className="smart-context-bar" role="region" aria-label="Suggested Context">
    {preview
      ? <NoteGlance preview={{ ...preview, detail: question }} className="smart-context-bar__note"/>
      : <><Icon name="context"/><span className="smart-context-bar__note-text"><strong>{question}</strong></span></>}
    <button type="button" className="btn btn-ghost smart-context-bar__action smart-context-bar__action--main" disabled={busy} onClick={count === 1 ? onAdd : onChoose}>{busy ? 'Adding…' : count === 1 ? 'Add' : 'Review'}</button>
    {count === 1 && <button type="button" className="btn btn-ghost smart-context-bar__action" disabled={busy} onClick={onChoose}>Other</button>}
    <button type="button" className="btn btn-ghost btn-square smart-context-bar__dismiss" aria-label="Dismiss suggestion" disabled={busy} onClick={onDismiss}><Icon name="close"/></button>
  </aside>
}
